import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { SearchInput } from 'app/design/primitives/input/search';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';

import type { SharingPlaceMember } from 'app/types/sharing-place';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Pressable, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

function Separator() {
  return <View style={{ height: 1, backgroundColor: theme.colors['surface.disabled'] }} />;
}

interface Properties {
  open: boolean;
  onClose: () => void;
  members: SharingPlaceMember[];
  navigation: SpecificNavigationProp;
}

export function PlaceMembersBottomSheet({ open, onClose, members, navigation }: Properties) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filteredMembers = (() => {
    if (!search.trim()) return members;
    const query = search.trim().toLowerCase();
    return members.filter((member) => {
      const firstName = member.user?.profile?.firstName?.toLowerCase() ?? '';

      const lastName = member.user?.profile?.lastName?.toLowerCase() ?? '';
      return firstName.includes(query) || lastName.includes(query) || `${firstName} ${lastName}`.includes(query);
    });
  })();

  const handleMemberPress = (member: SharingPlaceMember) => {
    onClose();
    if (member.userId) {
      navigateToUserProfile(navigation, member.userId);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <Column
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x5'],
        }}
      >
        <Text fontWeight="700" variant="headerXSmall" style={{ marginBottom: theme.spacing['spacing.general.x4'] }}>
          {t('place.membersTitle', { ns: 'neighbour-profile' })}
        </Text>

        <Column style={{ marginBottom: theme.spacing['spacing.general.x4'] }}>
          <SearchInput
            initialValue=""
            onSubmit={setSearch}
            searchAsYouType
            placeholder={t('place.searchMembers', { ns: 'neighbour-profile' })}
          />
        </Column>

        <FlatList
          data={filteredMembers}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <Pressable onPress={() => handleMemberPress(item)}>
              <Row
                alignItems="center"
                gap="spacing.general.x4"
                style={{ paddingVertical: theme.spacing['spacing.general.x3'] }}
              >
                <Thumbnail height={44} width={44} source={{ uri: item.user?.profile?.picture }} />
                <Column flex={1}>
                  <Text fontWeight="600" variant="bodyBase" numberOfLines={1}>
                    {[item.user?.profile?.firstName, item.user?.profile?.lastName].filter(Boolean).join(' ')}
                  </Text>
                </Column>
              </Row>
            </Pressable>
          )}
          ItemSeparatorComponent={Separator}
          ListEmptyComponent={
            <Text
              variant="bodyBase"
              color="text.tertiary"
              textAlign="center"
              style={{ marginTop: theme.spacing['spacing.general.x5'] }}
            >
              {t('place.noMembersFound', { ns: 'neighbour-profile' })}
            </Text>
          }
          showsVerticalScrollIndicator
          style={{ maxHeight: 420 }}
        />
      </Column>
    </BottomSheet>
  );
}
